<div class="h-full min-h-0 overflow-y-auto scrollbar-transparent">
  <div class="flex flex-col gap-4 pb-2">
    @if (balances(); as b) {
      <dl class="shrink-0 grid grid-cols-2 gap-3 m-0">
        <div class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
          <dt class="text-sm text-secondary m-0">Received</dt>
          <dd class="text-2xl font-semibold text-primary m-0 mt-1">
            {{ b.earnt | currency: currencyCode() }}
          </dd>
        </div>
        <div class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
          <dt class="text-sm text-secondary m-0">Pending</dt>
          <dd class="text-2xl font-semibold text-primary m-0 mt-1">
            {{ b.onItsWay | currency: currencyCode() }}
          </dd>
        </div>
      </dl>
    }

    @if (loadingState() === 'loading') {
      <p class="text-sm text-secondary m-0">Loading payouts…</p>
    } @else if (loadingState() === 'error') {
      <p class="text-sm text-red-600 m-0" role="alert">Failed to load payouts.</p>
    } @else if ((payouts() ?? []).length === 0) {
      <p class="text-sm text-secondary m-0">No payouts found.</p>
    } @else {
      <div class="data-table-wrapper w-full">
        <table class="data-table min-w-full">
          <thead>
            <tr>
              <th scope="col" class="text-left">Provider</th>
              <th scope="col" class="text-left">Amount</th>
              <th scope="col" class="text-left">Status</th>
              <th scope="col" class="text-left">Date</th>
              <th scope="col" class="text-left">Brand</th>
              <th scope="col" class="text-left">Brief</th>
              <th scope="col" class="relative w-10">
                <span class="sr-only">Expand</span>
              </th>
            </tr>
          </thead>
          <tbody>
            @for (item of payouts() ?? []; track payoutRowKey(item, $index)) {
              <tr
                class="cursor-pointer"
                (click)="toggleExpanded(item, $index)"
                [attr.aria-expanded]="isExpanded(item, $index)"
              >
                <td>
                  <p class="m-0 text-sm font-medium text-primary">
                    {{ payoutProviderLabel(item.provider) }}
                  </p>
                </td>
                <td>
                  <p class="m-0 text-sm font-medium text-primary">
                    {{ item.amount / 100 | currency: item.currencyCode.toUpperCase() }}
                  </p>
                </td>
                <td>
                  <span [class]="payoutStatusPillClass(item.payoutStatus)">
                    {{ payoutStatusLabel(item.payoutStatus) }}
                  </span>
                </td>
                <td>
                  <p class="m-0 text-sm text-secondary">
                    {{ item.createdDate | date: 'dd MMM yy HH:mm' }}
                  </p>
                </td>
                <td>
                  <p class="m-0 text-sm text-secondary">
                    {{ item.companyName ?? '—' }}
                  </p>
                </td>
                <td>
                  <p class="m-0 text-sm text-secondary">
                    {{ item.briefTitle ?? '—' }}
                  </p>
                </td>
                <td class="text-right">
                  <button
                    type="button"
                    class="inline-flex items-center justify-center rounded-md p-1 text-secondary hover:text-primary"
                    [attr.aria-expanded]="isExpanded(item, $index)"
                    [attr.aria-label]="isExpanded(item, $index) ? 'Collapse payout details' : 'Expand payout details'"
                    (click)="$event.stopPropagation(); toggleExpanded(item, $index)"
                  >
                    @if (isExpanded(item, $index)) {
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-5" aria-hidden="true">
                        <path d="m18 15-6-6-6 6" />
                      </svg>
                    } @else {
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-5" aria-hidden="true">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    }
                  </button>
                </td>
              </tr>
              @if (isExpanded(item, $index)) {
                <tr>
                  <td colspan="7" class="bg-zinc-50/80 dark:bg-zinc-900/40">
                    @let rowKey = payoutRowKey(item, $index);
                    <dl class="grid grid-cols-1 gap-3 px-4 py-3 m-0 sm:grid-cols-3">
                      <div>
                        <dt class="text-xs font-medium text-secondary m-0">Payout ID</dt>
                        <dd class="mt-1 m-0 flex items-start gap-2">
                          <span class="text-sm font-mono text-primary break-all flex-1 min-w-0">
                            {{ item.id ?? '—' }}
                          </span>
                          @if (item.id) {
                            <button
                              type="button"
                              class="inline-flex shrink-0 items-center justify-center rounded-md border border-zinc-200 p-1 text-secondary hover:bg-zinc-100 hover:text-primary dark:border-zinc-700 dark:hover:bg-zinc-800"
                              [attr.aria-label]="isCopied(getCopyKey(rowKey, 'payoutId')) ? 'Payout ID copied' : 'Copy payout ID'"
                              (click)="copyId($event, item.id, 'Payout ID', getCopyKey(rowKey, 'payoutId'))"
                            >
                              @if (isCopied(getCopyKey(rowKey, 'payoutId'))) {
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 lucide lucide-copy-check-icon lucide-copy-check" aria-hidden="true"><path d="m12 15 2 2 4-4"/><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                              } @else {
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 lucide lucide-copy-icon lucide-copy" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                              }
                            </button>
                          }
                        </dd>
                      </div>
                      <div>
                        <dt class="text-xs font-medium text-secondary m-0">Wallet transaction ID</dt>
                        <dd class="mt-1 m-0 flex items-start gap-2">
                          <span class="text-sm font-mono text-primary break-all flex-1 min-w-0">
                            {{ item.walletTransactionId ?? '—' }}
                          </span>
                          @if (item.walletTransactionId) {
                            <button
                              type="button"
                              class="inline-flex shrink-0 items-center justify-center rounded-md border border-zinc-200 p-1 text-secondary hover:bg-zinc-100 hover:text-primary dark:border-zinc-700 dark:hover:bg-zinc-800"
                              [attr.aria-label]="isCopied(getCopyKey(rowKey, 'walletTransactionId')) ? 'Wallet transaction ID copied' : 'Copy wallet transaction ID'"
                              (click)="copyId($event, item.walletTransactionId, 'Wallet transaction ID', getCopyKey(rowKey, 'walletTransactionId'))"
                            >
                              @if (isCopied(getCopyKey(rowKey, 'walletTransactionId'))) {
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 lucide lucide-copy-check-icon lucide-copy-check" aria-hidden="true"><path d="m12 15 2 2 4-4"/><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                              } @else {
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 lucide lucide-copy-icon lucide-copy" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                              }
                            </button>
                          }
                        </dd>
                      </div>
                      <div>
                        <dt class="text-xs font-medium text-secondary m-0">Brief application ID</dt>
                        <dd class="mt-1 m-0 flex items-start gap-2">
                          <span class="text-sm font-mono text-primary break-all flex-1 min-w-0">
                            {{ item.briefApplicationId ?? '—' }}
                          </span>
                          @if (item.briefApplicationId) {
                            <button
                              type="button"
                              class="inline-flex shrink-0 items-center justify-center rounded-md border border-zinc-200 p-1 text-secondary hover:bg-zinc-100 hover:text-primary dark:border-zinc-700 dark:hover:bg-zinc-800"
                              [attr.aria-label]="isCopied(getCopyKey(rowKey, 'briefApplicationId')) ? 'Brief application ID copied' : 'Copy brief application ID'"
                              (click)="copyId($event, item.briefApplicationId, 'Brief application ID', getCopyKey(rowKey, 'briefApplicationId'))"
                            >
                              @if (isCopied(getCopyKey(rowKey, 'briefApplicationId'))) {
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 lucide lucide-copy-check-icon lucide-copy-check" aria-hidden="true"><path d="m12 15 2 2 4-4"/><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                              } @else {
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 lucide lucide-copy-icon lucide-copy" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                              }
                            </button>
                          }
                        </dd>
                      </div>
                    </dl>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
    }
  </div>
</div>
